import { SubtitleTypography } from "@/components/common/Typographies";
import { ReportCard } from "@/sleep_compass_lite/components/target/ReportCard";
import { ReportCardComment } from "@/sleep_compass_lite/components/target/ReportCardComment";
import { ReportCardContent } from "@/sleep_compass_lite/components/target/ReportCardContent";
import { ReportQuestionTitle } from "@/sleep_compass_lite/components/target/ReportQuestionTitle";
import { ReportShowMore } from "@/sleep_compass_lite/components/target/ReportShowMore";
import { ReportTableContainer } from "@/sleep_compass_lite/components/target/ReportTableContainer";
import { SleepDaytimeReportTitle } from "@/sleep_compass_lite/components/target/SleepDaytimeReportTitle";
import {
  PX2,
  PX4,
  PX6,
} from "@/sleep_compass_lite/components/target/StackStyles";
import { StickyStack } from "@/sleep_compass_lite/components/target/StickyStack";
import { LiteReportHeaderProps } from "@/sleep_compass_lite/components/target/report/LiteReportMenuContext";
import {
  SleepDaytimeConditionBase,
  SleepDaytimeConditionItemBase,
} from "@/sleep_compass_lite/data_transfer_objects/target/report";
import { reportPath } from "@/sleep_compass_lite/domain_models/target/ReportPath";
import { useTargetAxios as useAxios } from "@/sleep_compass_lite/use_cases/target/common/UseTargetAxios";
import {
  Button,
  ButtonProps,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

/**
 * 日中の状態詳細情報
 */
interface DaytimeConditionItem extends SleepDaytimeConditionItemBase {
  /** あなたの結果 */
  result: string;
}

/**
 * 日中の状態情報
 */
interface DaytimeConditionInfo {
  /** 日中の眠気情報 */
  daytime_sleepiness: SleepDaytimeConditionBase<DaytimeConditionItem>;
  /** 日中の疲労感情報 */
  daytime_tiredness: SleepDaytimeConditionBase<DaytimeConditionItem>;
  /** パフォーマンス情報 */
  performance: SleepDaytimeConditionBase<DaytimeConditionItem>;
}

/**
 * 日中の眠気を表示します。
 *
 * @param props {@link DaytimeConditionInfo#daytime_sleepiness}
 * @returns 日中の眠気
 */
function DaytimeSleepinessSection(
  props: DaytimeConditionInfo["daytime_sleepiness"]
) {
  return (
    <ReportCard>
      <ReportCardContent>
        <Stack sx={{ gap: 2 }}>
          <SleepDaytimeReportTitle type="sleepiness" score={props.stars} />
          <ReportCardComment>{props.comment}</ReportCardComment>
          <ReportShowMore>
            <Stack sx={{ gap: 4 }}>
              <ReportQuestionTitle title="日中に眠気を感じましたか？" />
              <ReportTableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          p: 2,
                        }}
                      >
                        <Typography variant="caption">回答日</Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          p: 2,
                        }}
                      >
                        <Typography variant="caption">あなたの結果</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {props.items.map((v, i) => (
                      <TableRow key={i}>
                        <TableCell component="th" sx={{ py: 3, px: 2 }}>
                          <Typography variant="body2" noWrap>
                            {v.date}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 3, px: 2 }}>
                          <Typography
                            variant="body2"
                            sx={v.is_highlight ? { color: "error.main" } : {}}
                          >
                            {v.result}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ReportTableContainer>
            </Stack>
          </ReportShowMore>
        </Stack>
      </ReportCardContent>
    </ReportCard>
  );
}

/**
 * 日中の疲労感を表示します。
 *
 * @param props {@link DaytimeConditionInfo#daytime_tiredness}
 * @returns 日中の疲労感
 */
function DaytimeTiredness(props: DaytimeConditionInfo["daytime_tiredness"]) {
  return (
    <ReportCard>
      <ReportCardContent>
        <Stack sx={{ gap: 2 }}>
          <SleepDaytimeReportTitle type="tiredness" score={props.stars} />
          <ReportCardComment>{props.comment}</ReportCardComment>
          <ReportShowMore>
            <Stack sx={{ gap: 4 }}>
              <ReportQuestionTitle title="日中に疲労を感じましたか？" />
              <ReportTableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          p: 2,
                        }}
                      >
                        <Typography variant="caption">回答日</Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          p: 2,
                        }}
                      >
                        <Typography variant="caption">あなたの結果</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {props.items.map((v, i) => (
                      <TableRow key={i}>
                        <TableCell component="th" sx={{ py: 3, px: 2 }}>
                          <Typography variant="body2" noWrap>
                            {v.date}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 3, px: 2 }}>
                          <Typography
                            variant="body2"
                            sx={v.is_highlight ? { color: "error.main" } : {}}
                          >
                            {v.result}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ReportTableContainer>
            </Stack>
          </ReportShowMore>
        </Stack>
      </ReportCardContent>
    </ReportCard>
  );
}

/**
 * パフォーマンスを表示します。
 *
 * @param props {@link DaytimeConditionInfo#performance}
 * @returns パフォーマンス
 */
function Performance(props: DaytimeConditionInfo["performance"]) {
  return (
    <ReportCard>
      <ReportCardContent>
        <Stack sx={{ gap: 2 }}>
          <SleepDaytimeReportTitle type="performance" score={props.stars} />
          <ReportCardComment>{props.comment}</ReportCardComment>
          <ReportShowMore>
            <Stack sx={{ gap: 4 }}>
              <ReportQuestionTitle title="日中の活動（仕事など）の出来はどうでしたか？" />
              <ReportTableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          p: 2,
                        }}
                      >
                        <Typography variant="caption">回答日</Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          p: 2,
                        }}
                      >
                        <Typography variant="caption">あなたの結果</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {props.items.map((v, i) => (
                      <TableRow key={i}>
                        <TableCell component="th" sx={{ py: 3, px: 2 }}>
                          <Typography variant="body2" noWrap>
                            {v.date}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 3, px: 2 }}>
                          <Typography
                            variant="body2"
                            sx={v.is_highlight ? { color: "error.main" } : {}}
                          >
                            {v.result}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ReportTableContainer>
            </Stack>
          </ReportShowMore>
        </Stack>
      </ReportCardContent>
    </ReportCard>
  );
}

/**
 * 日中の状態の情報をサーバーから取得します。
 */
function useDaytimeConditionInfo({
  surveyInfoUid,
}: {
  surveyInfoUid?: string;
}) {
  const axios = useAxios();
  const [daytimeConditionInfo, setDaytimeConditionInfo] =
    useState<DaytimeConditionInfo>();
  useEffect(() => {
    (async () => {
      if (surveyInfoUid == null) return;
      const response: AxiosResponse<DaytimeConditionInfo> = await axios.get(
        `/api/lite/report/${surveyInfoUid}/daytime_condition/`
      );
      setDaytimeConditionInfo(response.data);
    })();
  }, [axios, surveyInfoUid]);
  return daytimeConditionInfo;
}

/**
 * 日中の状態を表示します。
 *
 * @returns 日中の状態
 */
export function DaytimeCondition({
  setLiteReportHeaderContext,
}: LiteReportHeaderProps) {
  const { surveyInfoUid } = useParams();
  const daytimeConditionInfo = useDaytimeConditionInfo({ surveyInfoUid });
  const navigate = useNavigate();

  const [isNextButtonFloating, setIsNextButtonFloating] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    // メニューバーの制御
    setLiteReportHeaderContext({
      title: "日中の状態",
      onClickBack() {
        if (surveyInfoUid == null) return;
        navigate(
          reportPath.getFullPath("SleepCondition", {
            ":surveyInfoUid": surveyInfoUid,
          })
        );
      },
    });
  }, [navigate, setLiteReportHeaderContext, surveyInfoUid]);

  const nextButtonSX: ButtonProps["sx"] = { my: 6 };
  if (isNextButtonFloating === false) {
    // ボタンの影は、フローティングしていないときは非表示(フローティングしているときは表示)
    nextButtonSX["boxShadow"] = "none";
  }

  if (surveyInfoUid == null || daytimeConditionInfo == null) return <></>;
  return (
    <Stack sx={{ backgroundColor: "background.default", pb: 14 }}>
      <Stack
        spacing={10}
        sx={{ px: PX6, pt: 6, pb: 10, backgroundColor: "white" }}
      >
        <img
          src="/img/image_report_stepper_03.svg"
          alt="Daytime condition step"
          loading="lazy"
          style={{ width: "100%", height: "auto" }}
        />
        <Stack sx={{ gap: "24px" }}>
          <Typography variant="h5">日中の状態とは</Typography>
          <Typography variant="body2">
            前日の睡眠の結果、日中の活動に支障がなかったのかを、2つの項目で評価しています。
          </Typography>
          <Stack sx={{ backgroundColor: "#EBF1F7", p: 4 }}>
            <img
              src="/img/image_lite_report_daytime_condition.png"
              alt="What's daytime condition"
              loading="lazy"
              style={{ width: "100%", height: "auto", maxWidth: "442.5px" }}
            />
          </Stack>
          <Typography variant="body2">
            必要な睡眠には個人差があるため、睡眠の状態が良くても、日中に強い眠気や疲労が続く場合はあなたにとっては睡眠が不十分な可能性があります。
          </Typography>
        </Stack>
      </Stack>
      <Stack sx={{ backgroundColor: "background.default", px: PX4 }}>
        <Stack sx={{ pt: 10, pb: 6, px: PX2 }}>
          <SubtitleTypography>あなたの結果</SubtitleTypography>
        </Stack>
        <Stack sx={{ gap: 4, pb: 6 }}>
          <DaytimeSleepinessSection
            {...daytimeConditionInfo.daytime_sleepiness}
          />
          <DaytimeTiredness {...daytimeConditionInfo.daytime_tiredness} />

          <Divider>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mx: 4 }}
            >
              その他の指標
            </Typography>
          </Divider>

          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              whiteSpace: "pre-wrap",
              color: "text.secondary",
            }}
          >
            {
              "今回の睡眠評価とは連動していませんが、\n睡眠と関係あるその他の指標です。"
            }
          </Typography>

          <Performance {...daytimeConditionInfo.performance} />
        </Stack>
      </Stack>
      <StickyStack
        sx={{
          px: PX4,
          background:
            "linear-gradient(180deg, rgba(246, 245, 244, 0.00) 0%, #F6F5F4 100%);",
        }}
        onChangeFloatingState={(isFloating) =>
          setIsNextButtonFloating(isFloating)
        }
      >
        <Button
          variant="contained"
          sx={nextButtonSX}
          component={Link}
          to={reportPath.getFullPath("PointsOfImprovement", {
            ":surveyInfoUid": surveyInfoUid,
          })}
        >
          次へ
        </Button>
      </StickyStack>
    </Stack>
  );
}
